import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  Input,
  Select,
  SimpleGrid,
  Spinner,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from '@chakra-ui/react';
import {
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  Tooltip,
} from 'chart.js';
import { Line } from 'react-chartjs-2';

import {
  Feed,
  FeedAnalysisIndicators,
  feedAnalysisService,
  FeedSample,
  REFERENCED_NUTRIENTS,
  ReferencedNutrientKey,
  SummaryRow,
} from '../../../../services/feedAnalysisService';
import { ResultsGrid } from '../components/ResultsGrid';
import {
  currentMonth,
  formatDate,
  formatPercent,
  monthShort,
  shiftMonth,
  STATUS_STYLE,
} from '../format';
import { PageShell } from '../PageShell';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
);

const CHART_TEXT = '#A0AEC0';
const CHART_GRID = 'rgba(160, 174, 192, 0.15)';

const chartOptions = (suffix = '') => ({
  responsive: true,
  maintainAspectRatio: false,
  interaction: { mode: 'index' as const, intersect: false },
  plugins: {
    legend: { labels: { color: CHART_TEXT } },
    datalabels: { display: false },
  },
  scales: {
    x: { ticks: { color: CHART_TEXT }, grid: { color: CHART_GRID } },
    y: {
      ticks: {
        color: CHART_TEXT,
        callback: (value: string | number) => `${value}${suffix}`,
      },
      grid: { color: CHART_GRID },
    },
  },
});

const Panel: React.FC<{ title: string; hint?: string }> = ({
  title,
  hint,
  children,
}) => (
  <Box
    bg="gray.800"
    borderWidth="1px"
    borderColor="gray.700"
    borderRadius="lg"
    p="4"
  >
    <Heading size="sm" mb={hint ? '1' : '3'}>
      {title}
    </Heading>
    {hint && (
      <Text fontSize="xs" color="gray.400" mb="3">
        {hint}
      </Text>
    )}
    {children}
  </Box>
);

const SummaryTable: React.FC<{
  rows: (Partial<SummaryRow> & {
    key: string;
    label: string;
    ideal: number;
    below: number;
    above: number;
    no_reference: number;
    ideal_rate: number | null;
  })[];
  firstColumn: string;
}> = ({ rows, firstColumn }) => (
  <Box overflowX="auto">
    <Table size="sm">
      <Thead>
        <Tr>
          <Th>{firstColumn}</Th>
          <Th isNumeric>% ideal</Th>
          <Th isNumeric color="green.300">
            {STATUS_STYLE.ideal.symbol}
          </Th>
          <Th isNumeric color="yellow.300">
            {STATUS_STYLE.below.symbol}
          </Th>
          <Th isNumeric color="red.300">
            {STATUS_STYLE.above.symbol}
          </Th>
          <Th isNumeric>Sem ref.</Th>
        </Tr>
      </Thead>
      <Tbody>
        {rows.map(row => (
          <Tr key={row.key}>
            <Td fontWeight="600">
              {row.label}
              {row.samples !== undefined && (
                <Text as="span" color="gray.500" fontWeight="400">
                  {' '}
                  ({row.samples})
                </Text>
              )}
            </Td>
            <Td isNumeric fontWeight="700">
              {formatPercent(row.ideal_rate)}
            </Td>
            <Td isNumeric>{row.ideal}</Td>
            <Td isNumeric>{row.below}</Td>
            <Td isNumeric>{row.above}</Td>
            <Td isNumeric>{row.no_reference}</Td>
          </Tr>
        ))}
      </Tbody>
    </Table>
  </Box>
);

const FeedAnalysisHistory: React.FC = () => {
  const [to, setTo] = useState(currentMonth());
  const [from, setFrom] = useState(shiftMonth(currentMonth(), -11));
  const [feedId, setFeedId] = useState('');
  const [nutrient, setNutrient] =
    useState<ReferencedNutrientKey>('crude_protein');
  const [feeds, setFeeds] = useState<Feed[]>([]);
  const [indicators, setIndicators] = useState<FeedAnalysisIndicators | null>(
    null,
  );
  const [samples, setSamples] = useState<FeedSample[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    feedAnalysisService
      .listFeeds()
      .then(setFeeds)
      .catch(() => setFeeds([]));
  }, []);

  const load = useCallback(async () => {
    if (from > to) return;
    setLoading(true);
    setError('');
    try {
      const filter = { from, to, feed_id: feedId || undefined };
      const [indicatorData, sampleData] = await Promise.all([
        feedAnalysisService.indicators(filter),
        feedAnalysisService.listSamples(filter),
      ]);
      setIndicators(indicatorData);
      setSamples(sampleData);
    } catch {
      setError('Não foi possível carregar o histórico.');
    } finally {
      setLoading(false);
    }
  }, [from, to, feedId]);

  useEffect(() => {
    load();
  }, [load]);

  const nutrientLabel =
    REFERENCED_NUTRIENTS.find(item => item.key === nutrient)?.label || '';

  const rateChart = useMemo(
    () =>
      indicators && {
        labels: indicators.months.map(item => monthShort(item.month)),
        datasets: [
          {
            label: '% ideal',
            data: indicators.months.map(item =>
              item.ideal_rate === null
                ? null
                : Math.round(item.ideal_rate * 1000) / 10,
            ),
            borderColor: '#4299E1',
            backgroundColor: '#4299E1',
            spanGaps: true,
            tension: 0.25,
          },
        ],
      },
    [indicators],
  );

  // resultado de cada amostra contra a faixa (mín/esperado/máx) da época
  const nutrientChart = useMemo(() => {
    const ordered = [...samples]
      .filter(item => item.evaluation[nutrient].result !== null)
      .sort((a, b) =>
        (a.collected_on || a.analysis_month).localeCompare(
          b.collected_on || b.analysis_month,
        ),
      );
    if (!ordered.length) return null;
    const series = (field: 'result' | 'min' | 'expected' | 'max') =>
      ordered.map(item => item.evaluation[nutrient][field]);
    return {
      labels: ordered.map(item =>
        feedId
          ? formatDate(item.collected_on) || monthShort(item.analysis_month)
          : `${item.feed_name} ${formatDate(item.collected_on)}`,
      ),
      datasets: [
        {
          label: 'Resultado',
          data: series('result'),
          borderColor: '#F6E05E',
          backgroundColor: ordered.map(item => {
            const { status } = item.evaluation[nutrient];
            if (status === 'below') return '#ECC94B';
            if (status === 'above') return '#F56565';
            if (status === 'ideal') return '#48BB78';
            return '#A0AEC0';
          }),
          pointRadius: 5,
          showLine: !!feedId,
        },
        {
          label: 'Esperado',
          data: series('expected'),
          borderColor: '#63B3ED',
          backgroundColor: '#63B3ED',
          pointRadius: 0,
          stepped: true,
        },
        {
          label: 'Mínimo',
          data: series('min'),
          borderColor: 'rgba(160, 174, 192, 0.6)',
          borderDash: [4, 4],
          pointRadius: 0,
          stepped: true,
        },
        {
          label: 'Máximo',
          data: series('max'),
          borderColor: 'rgba(160, 174, 192, 0.6)',
          borderDash: [4, 4],
          pointRadius: 0,
          stepped: true,
        },
      ],
    };
  }, [samples, nutrient, feedId]);

  return (
    <PageShell
      title="Relatórios e histórico"
      subtitle="Evolução dos resultados por ração e nutriente ao longo dos meses."
    >
      <SimpleGrid
        columns={{ base: 1, sm: 2, lg: 4 }}
        spacing="3"
        bg="gray.800"
        borderWidth="1px"
        borderColor="gray.700"
        borderRadius="lg"
        p="4"
        mb="6"
      >
        <FormControl>
          <FormLabel fontSize="xs">De</FormLabel>
          <Input
            type="month"
            size="sm"
            value={from}
            max={to}
            onChange={event => {
              event.target.value && setFrom(event.target.value);
            }}
          />
        </FormControl>
        <FormControl>
          <FormLabel fontSize="xs">Até</FormLabel>
          <Input
            type="month"
            size="sm"
            value={to}
            min={from}
            onChange={event => event.target.value && setTo(event.target.value)}
          />
        </FormControl>
        <FormControl>
          <FormLabel fontSize="xs">Ração</FormLabel>
          <Select
            size="sm"
            value={feedId}
            onChange={event => setFeedId(event.target.value)}
          >
            <option value="">Todas as rações</option>
            {feeds.map(feed => (
              <option key={feed.id} value={feed.id}>
                {feed.name}
              </option>
            ))}
          </Select>
        </FormControl>
        <FormControl>
          <FormLabel fontSize="xs">Nutriente do gráfico</FormLabel>
          <Select
            size="sm"
            value={nutrient}
            onChange={event => {
              setNutrient(event.target.value as ReferencedNutrientKey);
            }}
          >
            {REFERENCED_NUTRIENTS.map(item => (
              <option key={item.key} value={item.key}>
                {item.label}
              </option>
            ))}
          </Select>
        </FormControl>
      </SimpleGrid>

      {loading && (
        <Flex justify="center" py="10">
          <Spinner />
        </Flex>
      )}
      {!loading && error && <Text color="red.400">{error}</Text>}

      {!loading && !error && indicators && (
        <>
          <SimpleGrid columns={{ base: 1, xl: 2 }} spacing="4" mb="6">
            <Panel
              title="% ideal por mês"
              hint={`Período: ${formatPercent(
                indicators.totals.ideal_rate,
              )} ideal em ${indicators.totals.samples} amostra(s).`}
            >
              <Box h="260px">
                {rateChart && (
                  <Line data={rateChart} options={chartOptions('%')} />
                )}
              </Box>
            </Panel>
            <Panel
              title={`${nutrientLabel}: resultado × faixa`}
              hint={
                feedId
                  ? 'Ponto colorido pela situação; linhas tracejadas = faixa da referência vigente.'
                  : 'Escolha uma ração para ver a série com linha.'
              }
            >
              <Box h="260px">
                {nutrientChart ? (
                  <Line data={nutrientChart} options={chartOptions()} />
                ) : (
                  <Text color="gray.400" fontSize="sm">
                    Sem resultados de {nutrientLabel.toLowerCase()} no período.
                  </Text>
                )}
              </Box>
            </Panel>
          </SimpleGrid>

          <SimpleGrid columns={{ base: 1, xl: 2 }} spacing="4" mb="6">
            <Panel title="Por ração">
              <SummaryTable
                firstColumn="Ração (amostras)"
                rows={indicators.feeds.map(item => ({
                  ...item,
                  key: item.feed_id,
                  label: item.feed_name,
                }))}
              />
            </Panel>
            <Panel title="Por nutriente">
              <SummaryTable
                firstColumn="Nutriente"
                rows={indicators.nutrients.map(item => ({
                  ...item,
                  key: item.nutrient,
                }))}
              />
            </Panel>
          </SimpleGrid>

          <Heading size="md" fontWeight="600" mb="3">
            Amostras do período ({samples.length})
          </Heading>
          {samples.length ? (
            <ResultsGrid samples={samples} />
          ) : (
            <Text color="gray.400">Nenhuma amostra no período.</Text>
          )}
        </>
      )}
    </PageShell>
  );
};

export default FeedAnalysisHistory;
